import { useEffect, useState, useCallback } from 'react';
import { toast } from 'sonner';
import { TreeStructure, Plus, PencilSimple, TrashSimple, Download, Spinner } from '@phosphor-icons/react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { shizaiApi } from '@/pages/shizai/shizaiApi';
import { useAuth } from '@/auth/AuthContext';
import PartsRequestNotifyCard from '@/pages/settings/PartsRequestNotifyCard';
import { DataTable } from '@/components/ui/DataTable';

const emptyForm = () => ({ code: '', name: '', symbol: '', order_type: '', department_id: '', sort_order: 100, active: true, target_master: 'none' });

const TARGET_MASTER_OPTIONS = [
  { value: 'none', label: 'なし（履歴のみ記録）' },
  { value: 'mfg', label: '製造在庫マスター' },
  { value: 'eng', label: '技術部品マスター' },
  { value: 'it', label: 'IT資産' },
  { value: 'office', label: '事務資産' },
];
const TARGET_MASTER_CHIP = {
  mfg: { label: '製造', cls: 'bg-indigo-50 text-indigo-700 border-indigo-200' },
  eng: { label: '技術', cls: 'bg-teal-50 text-teal-700 border-teal-200' },
  it: { label: 'IT', cls: 'bg-sky-50 text-sky-700 border-sky-200' },
  office: { label: '事務', cls: 'bg-orange-50 text-orange-700 border-orange-200' },
};

/**
 * Admin-only settings page: manage the 受注分類 dropdown values used by
 * 部品受注入力 (parts request) form.
 */
export default function OrderCategoriesSettings() {
  const { user } = useAuth();
  const canManageGroups = user?.role === 'admin';
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(emptyForm());
  const [saving, setSaving] = useState(false);
  const [seeding, setSeeding] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await shizaiApi.orderCategories();
      setRows(r);
    } catch (_e) {
      toast.error('分類の取得に失敗しました');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const seedDefaults = async () => {
    if (!window.confirm(
      'H課 / S課 / ソルダリング / 社内受注 の初期データ 34件を流し込みます。\n\n' +
      '既存の同一コード行は最新の名称・記号・並び順に更新され、未登録行は追加されます。\n' +
      '削除は行われません（安全に何度でも実行可能）。\n\n実行しますか？'
    )) return;
    setSeeding(true);
    try {
      const res = await shizaiApi.seedDefaultOrderCategories();
      toast.success(`初期データ流し込み完了: 追加 ${res.inserted} / 更新 ${res.updated} / 変更なし ${res.skipped} (合計 ${res.total}件)`);
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || '初期データの流し込みに失敗しました');
    } finally {
      setSeeding(false);
    }
  };

  const openNew = () => { setEditing(null); setForm(emptyForm()); setDialogOpen(true); };
  const openEdit = (r) => {
    setEditing(r);
    setForm({
      code: r.code,
      name: r.name,
      symbol: r.symbol || '',
      order_type: r.order_type || '',
      sort_order: r.sort_order,
      active: r.active !== false,
      target_master: r.target_master || (r.engineering ? 'eng' : 'none'),
    });
    setDialogOpen(true);
  };

  const save = async () => {
    if (!form.code.trim() || !form.name.trim()) { toast.error('コードと名称は必須です'); return; }
    setSaving(true);
    try {
      const body = {
        code: form.code.trim(),
        name: form.name.trim(),
        symbol: form.symbol.trim() || null,
        order_type: form.order_type.trim() || null,
        sort_order: Number(form.sort_order || 0),
        active: !!form.active,
        engineering: !!form.engineering,
      };
      if (editing) {
        await shizaiApi.updateOrderCategory(editing.id, body);
        toast.success('更新しました');
      } else {
        await shizaiApi.createOrderCategory(body);
        toast.success('追加しました');
      }
      setDialogOpen(false);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || '保存に失敗しました');
    } finally {
      setSaving(false);
    }
  };

  const remove = async (r) => {
    if (!window.confirm(`「${r.name}」を削除しますか？`)) return;
    try {
      await shizaiApi.deleteOrderCategory(r.id);
      toast.success('削除しました');
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || '削除に失敗しました');
    }
  };

  return (
    <div className="space-y-5 page-enter" data-testid="settings-order-categories">
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div>
          <h1 className="text-2xl font-bold tracking-tight text-zinc-900 flex items-center gap-2">
            <TreeStructure size={26} weight="duotone" className="text-orange-500" />
            分類
          </h1>
          <p className="text-sm text-zinc-500 mt-1">
            <span className="font-medium">部品受注入力</span> フォームで依頼者が選択する分類のマスターです。
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Button
            onClick={seedDefaults}
            disabled={seeding}
            variant="outline"
            className="h-11 border-orange-300 text-orange-700 hover:bg-orange-50"
            data-testid="oc-seed-defaults"
            title="H課 / S課 / ソルダリング / 社内受注 / 共通購買 の初期37件を流し込みます（冪等）"
          >
            {seeding ? <Spinner size={16} className="mr-1.5 animate-spin" /> : <Download size={16} weight="bold" className="mr-1.5" />}
            初期データ流し込み
          </Button>
          <Button onClick={openNew} className="h-11 bg-teal-700 text-white shadow-md" data-testid="oc-new">
            <Plus size={16} weight="bold" className="mr-1.5" /> 新規追加
          </Button>
        </div>
      </div>

      {rows.length === 0 ? (
        <div className="bg-white rounded-xl border border-zinc-200 shadow-sm px-4 py-12 text-center">
          {loading ? (
            <div className="text-sm text-zinc-400">読込中...</div>
          ) : (
            <div className="flex flex-col items-center gap-3" data-testid="oc-empty">
              <TreeStructure size={40} weight="duotone" className="text-orange-300" />
              <div className="text-sm text-zinc-500">分類が登録されていません</div>
              <Button
                onClick={seedDefaults}
                disabled={seeding}
                className="bg-teal-700 text-white shadow-md"
                data-testid="oc-empty-seed"
              >
                {seeding ? <Spinner size={14} className="mr-1.5 animate-spin" /> : <Download size={14} weight="bold" className="mr-1.5" />}
                初期データ (34件) を流し込む
              </Button>
              <p className="text-xs text-zinc-400 max-w-md leading-relaxed">
                H課ヒーター / S課センスビー / ソルダリング / 社内受注 の標準マスターです。<br />
                後で個別に追加・編集・並び替え可能です。
              </p>
            </div>
          )}
        </div>
      ) : (
        <DataTable
          dataTestId="oc-list"
          rows={rows}
          rowKey={(r) => r.id}
          rowTestId={(r) => `oc-row-${r.id}`}
          columns={[
            { key: 'sort_order', header: '並び順', width: 'w-20', mobileHidden: true,
              cell: (r) => <span className="tabular-nums text-zinc-500">{String(r.sort_order ?? 0).padStart(4, '0')}</span> },
            { key: 'code', header: 'コード', cell: (r) => <span className="font-mono text-zinc-800">{r.code}</span> },
            { key: 'name', header: '分類', primary: true,
              cell: (r) => <span className="font-medium text-zinc-900">{r.name}</span> },
            { key: 'symbol', header: '記号', width: 'w-24', mobileBadge: true, sortable: false,
              cell: (r) => (r.symbol
                ? <span className="px-1.5 py-0.5 rounded bg-zinc-100 border border-zinc-200 text-[11px] font-bold font-mono">{r.symbol}</span>
                : <span className="text-zinc-300">—</span>) },
            { key: 'order_type', header: 'タイプ', width: 'w-40',
              cell: (r) => r.order_type || <span className="text-zinc-300">—</span> },
            { key: 'group_name', header: 'グループ', width: 'w-44',
              cell: (r) => (r.group_name
                ? (
                  <span data-testid={`oc-dept-${r.id}`} className="inline-flex items-center px-1.5 py-0.5 rounded text-[11px] font-semibold bg-violet-50 text-violet-700 border border-violet-200">
                    {r.group_name}
                  </span>
                )
                : <span className="text-zinc-300" data-testid={`oc-dept-${r.id}`}>—</span>) },
            { key: 'active', header: '状態', width: 'w-20', mobileBadge: true, sortable: false,
              cell: (r) => {
                const tm = r.target_master || (r.engineering ? 'eng' : 'none');
                const chip = TARGET_MASTER_CHIP[tm];
                return (
                  <span className="inline-flex items-center gap-1">
                    {r.active !== false
                      ? <span className="px-2 py-0.5 rounded bg-emerald-50 text-emerald-700 text-[11px] font-medium">有効</span>
                      : <span className="px-2 py-0.5 rounded bg-zinc-100 text-zinc-500 text-[11px] font-medium">無効</span>}
                    {chip && (
                      <span className={`px-1.5 py-0.5 rounded border text-[10px] font-bold ${chip.cls}`}
                        title={`入荷確認時に${TARGET_MASTER_OPTIONS.find((o) => o.value === tm)?.label}へ実購入価格を反映`}
                        data-testid={`oc-target-${r.id}`}>
                        {chip.label}
                      </span>
                    )}
                  </span>
                );
              } },
            { key: 'actions', header: '操作', actions: true,
              cell: (r) => (
                <>
                  <Button variant="outline" size="sm" className="h-8 px-2.5" onClick={() => openEdit(r)} data-testid={`oc-edit-${r.id}`}>
                    <PencilSimple size={12} className="mr-1" /> 編集
                  </Button>
                  <Button variant="outline" size="sm" className="h-8 w-8 p-0 text-rose-600 hover:bg-rose-50" onClick={() => remove(r)} data-testid={`oc-delete-${r.id}`}>
                    <TrashSimple size={12} />
                  </Button>
                </>
              ) },
          ]}
          empty={{ text: '分類が登録されていません' }}
        />
      )}

      <Dialog open={dialogOpen} onOpenChange={(o) => !o && setDialogOpen(false)}>
        <DialogContent className="max-w-md" aria-describedby={undefined} data-testid="oc-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <TreeStructure size={20} weight="duotone" className="text-orange-500" />
              {editing ? '分類を編集' : '分類を追加'}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-xs font-semibold text-zinc-600">コード ＊</Label>
                <Input value={form.code} onChange={(e) => setForm({ ...form, code: e.target.value })}
                       placeholder="例: PROD_A" className="h-10 mt-1 font-mono" data-testid="oc-code" />
              </div>
              <div>
                <Label className="text-xs font-semibold text-zinc-600">記号</Label>
                <Input value={form.symbol} onChange={(e) => setForm({ ...form, symbol: e.target.value })}
                       placeholder="例: P" maxLength={8} className="h-10 mt-1 font-mono text-center font-bold uppercase" data-testid="oc-symbol" />
              </div>
            </div>
            <div>
              <Label className="text-xs font-semibold text-zinc-600">分類 (名称) ＊</Label>
              <Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })}
                     placeholder="例: 製品A" className="h-10 mt-1" data-testid="oc-name" />
            </div>
            <div>
              <Label className="text-xs font-semibold text-zinc-600">タイプ</Label>
              <Input value={form.order_type} onChange={(e) => setForm({ ...form, order_type: e.target.value })}
                     placeholder="例: 製品 / 試作 / サービス / 修繕 …" className="h-10 mt-1" data-testid="oc-order-type" />
              <p className="text-[11px] text-zinc-400 mt-1">経理向けの分類ラベル（自由記入）。TECHS互換のため任意項目です。</p>
            </div>
            <div>
              <Label className="text-xs font-semibold text-zinc-600">並び順</Label>
              <Input type="number" value={form.sort_order} onChange={(e) => setForm({ ...form, sort_order: e.target.value })}
                     className="h-10 mt-1 tabular-nums" data-testid="oc-sort" />
              <p className="text-[11px] text-zinc-400 mt-1">数字が小さいほど上に表示されます</p>
            </div>
            <div className="flex items-center justify-between rounded-md border border-zinc-200 bg-zinc-50 px-3 py-2">
              <Label className="text-xs font-semibold text-zinc-700">有効</Label>
              <Switch checked={form.active} onCheckedChange={(v) => setForm({ ...form, active: v })} data-testid="oc-active" />
            </div>
            <div className="rounded-md border border-teal-200 bg-teal-50/60 px-3 py-2">
              <Label className="text-xs font-semibold text-teal-800">反映先マスター（入荷確認時の価格反映先）</Label>
              <select
                value={form.target_master}
                onChange={(e) => setForm({ ...form, target_master: e.target.value })}
                className="mt-1.5 h-10 w-full rounded-md border border-teal-300 bg-white px-2 text-sm text-zinc-800 outline-none focus:ring-2 focus:ring-teal-200"
                data-testid="oc-target-master"
              >
                {TARGET_MASTER_OPTIONS.map((o) => (
                  <option key={o.value} value={o.value}>{o.label}</option>
                ))}
              </select>
              <p className="text-[11px] text-teal-700/80 mt-1">
                この分類の依頼が「入荷済」になったとき、実際購入金額を選択したマスターの原価へ自動反映します。
                「技術部品マスター」を選ぶと、入荷時に技術資産への自動登録も行われます。
              </p>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDialogOpen(false)}>キャンセル</Button>
            <Button onClick={save} disabled={saving} className="bg-teal-700 hover:bg-teal-800" data-testid="oc-save">
              {saving ? '保存中...' : '保存'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <div className="pt-2 border-t border-zinc-200">
        <PartsRequestNotifyCard canManage={canManageGroups} />
      </div>
    </div>
  );
}
